import { AskPdfApi, AxiosMethods } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';

import {
  ConversationCreated,
  DocumentIndexStatus,
  DocumentInsights,
  DocumentStatus
} from './Types';

export enum AskPdfQueryKey {
  DOCUMENT_STATUS = 'ask_pdf_document_status',
  DOCUMENT_INSIGHTS = 'ask_pdf_document_insights'
}

/** The assistant service is deployed separately from the main API. */
const baseURL = process.env.ASK_PDF_API_BASE_URL;

const withResource = (endpoint: string, resourceId: string) =>
  endpoint.replace(':resourceId', resourceId);

/**
 * Ingest runs on a schedule, so a freshly uploaded PDF may not be
 * queryable yet. Poll while it is pending so the dialog can move from
 * "processing" to ready without the user reopening it.
 */
export const getDocumentStatusQuery = (resourceId: string) => ({
  queryKey: [AskPdfQueryKey.DOCUMENT_STATUS, resourceId],
  queryFn: (): Promise<DocumentStatus> =>
    axiosHelper({
      endpoint: withResource(AskPdfApi.GetStatus, resourceId),
      method: AxiosMethods.GET,
      baseURL
    }),
  refetchInterval: (data?: DocumentStatus) =>
    data?.status === DocumentIndexStatus.Pending ? 5000 : (false as const)
});

export const getDocumentInsightsQuery = (resourceId: string) => ({
  queryKey: [AskPdfQueryKey.DOCUMENT_INSIGHTS, resourceId],
  queryFn: (): Promise<DocumentInsights> =>
    axiosHelper({
      endpoint: withResource(AskPdfApi.GetInsights, resourceId),
      method: AxiosMethods.GET,
      baseURL
    }),
  staleTime: Infinity
});

/**
 * Nudges the document to the front of the ingest queue. Safe to call
 * repeatedly — the server treats it as idempotent.
 */
export const ensureDocumentIndexed = (resourceId: string): Promise<void> =>
  axiosHelper({
    endpoint: withResource(AskPdfApi.EnsureIndexed, resourceId),
    method: AxiosMethods.POST,
    baseURL
  });

export const createConversation = (
  resourceId: string
): Promise<ConversationCreated> =>
  axiosHelper({
    endpoint: withResource(AskPdfApi.CreateConversation, resourceId),
    method: AxiosMethods.POST,
    baseURL
  });

export * from './Types';
export * from './stream';
